import { useEffect, useMemo, useRef, useState } from "react";
import { useUnits } from "../context/UnitContext";
import {
    calculateDailyWaterMl,
    lbsToKg,
    kgToLbs,
    formatVolume,
    calculateGlasses,
    buildHourlySchedule,
    mlToFloz,
} from "../lib/calculations";
import { Droplet, Zap, Sun, Snowflake, CloudSun, CloudFog } from "lucide-react";

const climates = [
    { value: "cold", label: "Cold", icon: Snowflake },
    { value: "temperate", label: "Temperate", icon: CloudSun },
    { value: "warm", label: "Warm", icon: Sun },
    { value: "hot", label: "Hot", icon: Sun },
    { value: "humid", label: "Humid", icon: CloudFog },
];

const activities = [
    { value: "sedentary", label: "Sedentary", desc: "Mostly sitting" },
    { value: "light", label: "Light", desc: "Light walking" },
    { value: "moderate", label: "Moderate", desc: "30-60 min/day" },
    { value: "active", label: "Active", desc: "1-2 hr/day" },
    { value: "athlete", label: "Athlete", desc: "Daily training" },
];

const ResultsPanel = ({ totalMl, units }) => {
    const glasses = calculateGlasses(totalMl);
    const schedule = useMemo(() => buildHourlySchedule(totalMl), [totalMl]);
    const perGlass = Math.round(totalMl / glasses);

    return (
        <div className="bg-primary text-white p-6 sm:p-10 h-full flex flex-col" data-testid="results-card">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/70 mb-4">
                Your daily target
            </div>

            <div className="mb-8">
                <div className="text-6xl sm:text-7xl font-black tracking-tighter leading-none" data-testid="results-total">
                    {formatVolume(totalMl, units)}
                </div>
                <div className="text-sm mt-3 text-white/80 font-mono uppercase tracking-widest">
                    {units === "metric"
                        ? `${mlToFloz(totalMl).toFixed(0)} fl oz`
                        : `${totalMl} ml`}{" "}
                    · {glasses} glasses · ~{perGlass} ml each
                </div>
            </div>

            <div className="mb-6">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/70 mb-3">
                    Glass progress
                </div>
                <div className="grid grid-cols-8 gap-1.5" data-testid="glasses-grid">
                    {Array.from({ length: glasses }).map((_, i) => (
                        <div
                            key={i}
                            className="h-10 bg-white/15 border border-white/40 relative overflow-hidden"
                        >
                            <div className="absolute inset-x-0 bottom-0 bg-white" style={{ height: "75%" }} />
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-auto">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/70 mb-3">
                    Hourly schedule (7am - 10pm)
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-8 gap-px bg-white/30" data-testid="schedule-grid">
                    {schedule.slice(0, 16).map((s) => (
                        <div key={s.hour} className="bg-primary text-center py-2 px-1">
                            <div className="text-[10px] font-mono text-white/70">
                                {s.label.replace(":00 ", "")}
                            </div>
                            <div className="text-xs font-bold">
                                {units === "metric" ? `${s.ml}ml` : `${mlToFloz(s.ml).toFixed(0)}oz`}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export const MainCalculator = () => {
    const { units, setUnits } = useUnits();
    const [weight, setWeight] = useState(units === "metric" ? 70 : 154);
    const [age, setAge] = useState(30);
    const [activity, setActivity] = useState("moderate");
    const [climate, setClimate] = useState("temperate");
    const [pregnant, setPregnant] = useState(false);
    const [breastfeeding, setBreastfeeding] = useState(false);

    // Auto-convert weight value when user toggles units (skip first render)
    const prevUnitsRef = useRef(units);
    useEffect(() => {
        if (prevUnitsRef.current === units) return;
        const v = Number(weight);
        if (!isNaN(v) && v > 0) {
            if (units === "imperial" && prevUnitsRef.current === "metric") {
                setWeight(Math.round(kgToLbs(v)));
            } else if (units === "metric" && prevUnitsRef.current === "imperial") {
                setWeight(Math.round(lbsToKg(v)));
            }
        }
        prevUnitsRef.current = units;
    }, [units, weight]);

    const weightKg = units === "metric" ? Number(weight) : lbsToKg(Number(weight));

    const totalMl = useMemo(
        () =>
            calculateDailyWaterMl({
                weightKg,
                activityLevel: activity,
                climate,
                age: Number(age),
                pregnant,
                breastfeeding,
            }),
        [weightKg, activity, climate, age, pregnant, breastfeeding]
    );

    return (
        <div className="grid grid-cols-1 lg:grid-cols-5 border border-foreground bg-card hard-shadow" data-testid="main-calculator">
            {/* FORM 60% */}
            <div className="lg:col-span-3 p-6 sm:p-10 lg:p-12 border-b lg:border-b-0 lg:border-r border-foreground">
                <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
                    <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4 text-primary" strokeWidth={2.5} />
                        <span className="editorial-label">Daily intake calculator</span>
                    </div>
                    <div
                        className="inline-flex border border-foreground bg-background"
                        data-testid="calculator-unit-toggle"
                        role="group"
                        aria-label="Choose measurement units"
                    >
                        <button
                            type="button"
                            onClick={() => setUnits("metric")}
                            data-testid="calc-units-metric"
                            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${
                                units === "metric" ? "bg-primary text-white" : "hover:bg-secondary"
                            }`}
                        >
                            Metric · kg / ml
                        </button>
                        <button
                            type="button"
                            onClick={() => setUnits("imperial")}
                            data-testid="calc-units-imperial"
                            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border-l border-foreground transition-colors ${
                                units === "imperial" ? "bg-primary text-white" : "hover:bg-secondary"
                            }`}
                        >
                            Imperial · lbs / oz
                        </button>
                    </div>
                </div>
                <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-8">
                    Your numbers, in real time.
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-8">
                    <div>
                        <label className="editorial-label block mb-2" htmlFor="weight">
                            Weight ({units === "metric" ? "kg" : "lbs"})
                        </label>
                        <input
                            id="weight"
                            data-testid="weight-input"
                            type="number"
                            min="20"
                            max={units === "metric" ? 200 : 440}
                            value={weight}
                            onChange={(e) => setWeight(e.target.value)}
                            className="editorial-input"
                        />
                    </div>
                    <div>
                        <label className="editorial-label block mb-2" htmlFor="age">
                            Age (years)
                        </label>
                        <input
                            id="age"
                            data-testid="age-input"
                            type="number"
                            min="10"
                            max="100"
                            value={age}
                            onChange={(e) => setAge(e.target.value)}
                            className="editorial-input"
                        />
                    </div>
                </div>

                <div className="mb-8">
                    <div className="editorial-label mb-3">Activity level</div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-foreground border border-foreground [&>button:last-child]:col-span-2 sm:[&>button:last-child]:col-span-1" data-testid="activity-group">
                        {activities.map((a) => (
                            <button
                                key={a.value}
                                type="button"
                                data-testid={`activity-${a.value}`}
                                onClick={() => setActivity(a.value)}
                                className={`p-3 text-left transition-colors ${
                                    activity === a.value
                                        ? "bg-foreground text-background"
                                        : "bg-card hover:bg-secondary"
                                }`}
                            >
                                <div className="text-sm font-bold uppercase tracking-tight">{a.label}</div>
                                <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">{a.desc}</div>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mb-8">
                    <div className="editorial-label mb-3">Climate</div>
                    <div className="grid grid-cols-5 gap-px bg-foreground border border-foreground" data-testid="climate-group">
                        {climates.map((c) => {
                            const Icon = c.icon;
                            return (
                                <button
                                    key={c.value}
                                    type="button"
                                    data-testid={`climate-${c.value}`}
                                    onClick={() => setClimate(c.value)}
                                    className={`p-3 flex flex-col items-center justify-center gap-1 transition-colors ${
                                        climate === c.value
                                            ? "bg-foreground text-background"
                                            : "bg-card hover:bg-secondary"
                                    }`}
                                >
                                    <Icon className="h-4 w-4" />
                                    <span className="text-[10px] font-mono uppercase tracking-widest">{c.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="flex flex-wrap gap-3">
                    <button
                        type="button"
                        data-testid="toggle-pregnant"
                        onClick={() => {
                            setPregnant(!pregnant);
                            if (!pregnant) setBreastfeeding(false);
                        }}
                        className={`text-xs font-mono uppercase tracking-widest border border-foreground px-4 py-2 transition-colors ${
                            pregnant ? "bg-foreground text-background" : "bg-card hover:bg-secondary"
                        }`}
                    >
                        + Pregnant
                    </button>
                    <button
                        type="button"
                        data-testid="toggle-breastfeeding"
                        onClick={() => {
                            setBreastfeeding(!breastfeeding);
                            if (!breastfeeding) setPregnant(false);
                        }}
                        className={`text-xs font-mono uppercase tracking-widest border border-foreground px-4 py-2 transition-colors ${
                            breastfeeding ? "bg-foreground text-background" : "bg-card hover:bg-secondary"
                        }`}
                    >
                        + Breastfeeding
                    </button>
                </div>
            </div>

            {/* RESULTS 40% */}
            <div className="lg:col-span-2">
                <ResultsPanel totalMl={totalMl} units={units} />
            </div>
        </div>
    );
};
